import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { deleteSpaces } from "app/utils";
import { useNavigate, useParams } from "react-router-dom";
import { useEncargadosContext } from "../encargados-context";
import { EncargadoProvider, useEncargadoContext } from "./encargado-context";
import { useResourcesContext } from "app/contexts/resources";
import {
  celularRegex,
  correoElectronicoRegex,
  identificacionRegex,
  nombreCompletoRegex,
  rtnRegex,
  telefonoRegex,
} from "app/utils/regexps";
import { MdArrowBack } from "react-icons/md";

export function EncargadoForm() {
  return (
    <EncargadoProvider>
      <Form />
    </EncargadoProvider>
  );
}

function Form() {
  const params = useParams();
  const navigate = useNavigate();

  const { generos, cargos } = useResourcesContext();

  const { addEncargado, isAdding, updateEncargado, isUpdating, cancel } =
    useEncargadosContext();

  const { encargado, setEncargado, isEditing } = useEncargadoContext();

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      if (!!isEditing) {
        return await updateEncargado({
          encargadoId: params.encargado_id,
          encargado,
        });
      } else {
        return await addEncargado({ encargado });
      }
    } catch (error) {
      console.log(error.message);
    }
  }

  return (
    <Container maxWidth="sm" className="animate__animated animate__fadeIn">
      <Paper
        component="form"
        onSubmit={handleSubmit}
        onReset={cancel}
        sx={{ padding: 2 }}
        variant="outlined"
      >
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Tooltip title="Atrás">
                  <IconButton
                    onClick={() => navigate("../..", { replace: true })}
                  >
                    <MdArrowBack />
                  </IconButton>
                </Tooltip>

                <Typography variant="h6">
                  {`${!!isEditing ? "Editar" : "Agregar"} encargado`}
                </Typography>
              </Stack>

              {!!isEditing && (
                <Chip
                  label={
                    !!encargado.encargado_verificado
                      ? "Verificado"
                      : "Pendiente"
                  }
                  color={
                    !!encargado.encargado_verificado ? "success" : "warning"
                  }
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <FormControl
              size="small"
              fullWidth
              required
              disabled={!!encargado.encargado_verificado}
            >
              <InputLabel id="cargos-label">Cargos</InputLabel>

              <Select
                labelId="cargos-label"
                multiple
                value={encargado.cargos || []}
                onChange={(e) =>
                  setEncargado((encargado) => ({
                    ...encargado,
                    cargos: e.target.value,
                  }))
                }
                input={<OutlinedInput label="Chip" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((cargo_id) => {
                      const cargo = cargos.results.find(
                        (result) => result.cargo_id === cargo_id
                      );

                      return <Chip key={cargo_id} label={cargo.cargo_nombre} />;
                    })}
                  </Box>
                )}
              >
                {cargos.results.map((result) => (
                  <MenuItem key={result.cargo_id} value={result.cargo_id}>
                    <Checkbox
                      checked={
                        !!encargado.cargos.find(
                          (cargo_id) => cargo_id === result.cargo_id
                        )
                      }
                    />

                    <ListItemText primary={result.cargo_nombre} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl
              size="small"
              fullWidth
              required
              disabled={!!encargado.encargado_verificado}
            >
              <InputLabel id="departamento-label">Género</InputLabel>

              <Select
                label="Género"
                labelId="departamento-label"
                value={encargado.genero_id || ""}
                onChange={(e) =>
                  setEncargado((encargado) => ({
                    ...encargado,
                    genero_id: e.target.value,
                  }))
                }
              >
                {generos.results.map((genero) => (
                  <MenuItem key={genero.genero_id} value={genero.genero_id}>
                    {genero.genero_nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="text"
              label="Nombre completo"
              value={encargado.encargado_nombre_completo || ""}
              onChange={(e) =>
                setEncargado((encargado) => ({
                  ...encargado,
                  encargado_nombre_completo: e.target.value,
                }))
              }
              inputProps={{
                pattern: nombreCompletoRegex.pattern,
                title: nombreCompletoRegex.title,
              }}
              size="small"
              fullWidth
              required
              disabled={!!encargado.encargado_verificado}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="text"
              label="Número de identidad, pasaporte o carnet de residente"
              value={encargado.encargado_identificacion || ""}
              onChange={(e) =>
                setEncargado((encargado) => ({
                  ...encargado,
                  encargado_identificacion: deleteSpaces(e.target.value),
                }))
              }
              inputProps={{
                pattern: identificacionRegex.pattern,
                title: identificacionRegex.title,
              }}
              size="small"
              fullWidth
              required
              disabled={!!encargado.encargado_verificado}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="text"
              label="Número de RTN"
              value={encargado.encargado_rtn || ""}
              inputProps={{
                pattern: rtnRegex.pattern,
                title: rtnRegex.title,
              }}
              onChange={(e) =>
                setEncargado((encargado) => ({
                  ...encargado,
                  encargado_rtn: deleteSpaces(e.target.value),
                }))
              }
              size="small"
              fullWidth
              required
              disabled={!!encargado.encargado_verificado}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="text"
              label="Número de teléfono (opcional)"
              value={encargado.encargado_telefono || ""}
              onChange={(e) =>
                setEncargado((encargado) => ({
                  ...encargado,
                  encargado_telefono: deleteSpaces(e.target.value),
                }))
              }
              inputProps={{
                pattern: telefonoRegex.pattern,
                title: telefonoRegex.title,
              }}
              size="small"
              fullWidth
              disabled={!!encargado.encargado_verificado}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="text"
              label="Número de celular"
              value={encargado.encargado_celular || ""}
              onChange={(e) =>
                setEncargado((encargado) => ({
                  ...encargado,
                  encargado_celular: deleteSpaces(e.target.value),
                }))
              }
              inputProps={{
                pattern: celularRegex.pattern,
                title: celularRegex.title,
              }}
              size="small"
              fullWidth
              required
              disabled={!!encargado.encargado_verificado}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="text"
              label="Dirección de correo electrónico (opcional)"
              value={encargado.encargado_correo_electronico || ""}
              onChange={(e) =>
                setEncargado((encargado) => ({
                  ...encargado,
                  encargado_correo_electronico: deleteSpaces(e.target.value),
                }))
              }
              inputProps={{
                pattern: correoElectronicoRegex.pattern,
                title: correoElectronicoRegex.title,
              }}
              size="small"
              fullWidth
              disabled={!!encargado.encargado_verificado}
            />
          </Grid>

          {!encargado.encargado_verificado && (
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="end" spacing={1}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="small"
                  color={!!isEditing ? "warning" : "success"}
                  loading={!!isUpdating || !!isAdding}
                >
                  {!!isEditing ? "Actualizar" : "Agregar"}
                </LoadingButton>

                <Button
                  type="reset"
                  variant="outlined"
                  size="small"
                  color="inherit"
                  disabled={!!isUpdating || !!isAdding}
                >
                  Cancelar
                </Button>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Container>
  );
}
